import { ReactNode, useCallback, useState } from 'react'
import ListPager, { LIST_PAGE_SIZE } from '@client/components/ListPager'
import { useTranslation } from 'react-i18next'
import { Container, SortType } from 'rsuite'
import ListMenu from '@client/components/ListMenu'
import { PeoplesIcon } from '@/client/config'
import { useLayout } from '@/client/components/Layout/LayoutManager'
import { Button, Table } from '../../components/Controls'
import UserModel, { TUser, userTabName } from '../../models/user'
import EditUser from './EditUser'
import { RoleType } from '@/shared'
import { SidebarTopProps } from '@client/stores/tabsStore'
import { useContextMenuTable } from '@/client/utils/useContextMenu'
import useSidebarInvalidation from '@client/utils/useSidebarInvalidation.ts'
import useSidebarQueryList from '@client/utils/useSidebarQueryList.ts'
import useListContextMenuItems from '@client/utils/useListContextMenuItems.ts'

//console.log('Load UserList ...')

const userModel = new UserModel()

export default function SidebarUsers() {
    const { t } = useTranslation()
    const [sortColumn, setSortColumn] = useState<string>('name');
    const [sortType, setSortType] = useState<SortType | undefined>('asc');
    const [page, setPage] = useState<number>(1)
    // deactivated users only behind the switch, and then only they
    const [showDisabled, setShowDisabled] = useState<boolean>(false)
    const { addTab, onContextMenu, openTabToRight, removeTabCopies, removeOtherTabs } = useLayout()
    const { rows, loading, error, data, refetch } = useSidebarQueryList<TUser, { page: number; count: number, sortField: string, sortOrder: SortType, disabled?: string }>({
        queryFn: userModel.list,
        // users have no 'name' — sorting by it left the order to chance, and a paged
        // list then shows a user twice or never
        args: {
            page, count: LIST_PAGE_SIZE, sortField: sortColumn === 'name' ? 'lastName' : sortColumn, sortOrder: sortType || 'asc',
            disabled: showDisabled ? '1' : undefined,
        },
    })

    useSidebarInvalidation({
        tables: ['user'],
        actions: ['updated', 'deleted'],
        refetch,
    })

    const canSort = rows.length > 0

    const controller = {
        defaultRow(params?: any) {
            return {
                _id: '',
                userName: '',
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                password: '',
                confirmPassword: '',
                settings: {
                    language: '',
                    openProject: '',
                },
                role: RoleType.Viewer,
            }
        },

        getTabContent(params: any) {
            const { tab } = params
            return <EditUser tabId={tab.id} onCreated={handleUserCreated} />
        },

        useProjectInBreadCrumb(params: any) {
            return false
        },

        getBreadCrumb(params: any) {
            const { tab } = params
            const breadCrumb: string[] = [t('Users'), tab.name]
            return breadCrumb
        },

        ignoreProjectExists(params: any) {
            return true
        },
    }

    function onAddUser(): void {
        onOpenTab(controller.defaultRow())
    }

    const handleUserCreated = (newUser: any) => {
        refetch()
    }

    function newTab(row: TUser) {
        let name
        if (!row._id) {
            name = t('New user')
        } else {
            name = userTabName(row)
        }
        return {
            dataId: `user-${row._id}`,
            name,
            component: 'user',
            controller,
            icon: <PeoplesIcon />,
            store: {
                id: row._id,
                initialData: row,
                formData: row,
            },
        }
    }

    function onOpenTab(row: TUser) {
        addTab(newTab({ ...controller.defaultRow(), ...row }))
    }

    function onSort(column: string, sortOrder?: SortType) {
        setPage(1)
        setSortColumn(column)
        setSortType(sortOrder || 'asc')
        refetch()
    }

    function onRenderLoading(loading: ReactNode): ReactNode {
        return null
    }

    function onRenderEmpty() {
        return <div className="rs-table-body-info">{t('No users found.')}</div>
    }

    function SidebarTop({ data }: SidebarTopProps) {
        return (
            <Container className="tree-top">
                <Button appearance="ghost" onClick={onAddUser}>
                    {t('Add user')}
                </Button>
                <ListMenu
                    items={[
                        {
                            label: t('Deactivated users'),
                            checked: showDisabled,
                            onClick: () => {
                                setPage(1)
                                setShowDisabled(!showDisabled)
                                refetch()
                            },
                        },
                    ]}
                />
            </Container>
        )
    }

    const getUserById = useCallback(
        (id: string): TUser | null => {
            return rows.find((u) => u._id === id) || null
        },
        [rows]
    )

    const { show } = useListContextMenuItems<TUser>({
        t,
        onContextMenu,
        addTab,
        openTabToRight,
        removeTabCopies,
        removeOtherTabs,
        getItemById: getUserById,
        createTab: newTab,
    })

    const handleRowContextMenu = useContextMenuTable<TUser>(show, (row) => ({
        id: row._id,
    }))

    return (
        <div className="sidebar-content sidebar-content-users">
            <div className="sidebar-content-body">
                <SidebarTop data={data} />
                <Table
                    className={'tablist-table' + (rows.length == 0 ? ' empty-list' : '')}
                    loading={loading}
                    wordWrap="break-word"
                    fillHeight
                    data={rows}
                    renderEmpty={onRenderEmpty}
                    renderLoading={onRenderLoading}
                    sortColumn={sortColumn}
                    sortType={sortType}
                    onSortColumn={onSort}
                    onRowClick={onOpenTab}
                    onRowContextMenu={handleRowContextMenu}
                >
                    <Table.Column align="left" sortable={canSort} flexGrow={1}>
                        <Table.HeaderCell className="name">{t('Name')}</Table.HeaderCell>
                        <Table.Cell className="name" dataKey="lastName">
                            {(row: TUser) => {
                                return row.lastName + ' ' + row.firstName
                            }}
                        </Table.Cell>
                    </Table.Column>
                </Table>
            <ListPager total={Number(data?.total ?? 0)} page={page} onChangePage={(p) => { setPage(p); refetch() }} disabled={loading} />
            </div>
        </div>
    )
}
